import { useCallback, useEffect, useRef, useState } from "react";

import { LuzmoDashboardComponent } from "@luzmo/react-embed";
import { Box } from "@mui/material";
import { captureException } from "@sentry/react";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent, EVOTRUX_SURFACE_WHITE } from "constants/index";

import { EmbeddedDashboardSkeleton } from "../EmbeddedDashboardSkeleton";
import {
  EmbeddedDashboardActionType,
  getDashboardActionUrl,
  navigateInThisTab,
  openInNewTab,
  readDashboardAction,
  readDashboardActionFromMessage,
  readEmbedErrorMessage,
  readMeasureDimensionPickerUpdate,
  readRenderedScreenMode,
} from "./utils";

import type { LuzmoEmbedDashboard } from "@luzmo/embed";

import type {
  EmbeddedDashboardAction,
  EmbeddedDashboardScreenMode,
  MeasureDimensionPickerUpdate,
} from "./utils";

interface Props {
  payload: Record<string, string>;
  /** A table's column picker changed; the page decides whether to save it. */
  onMeasureDimensionPickerChange?: (
    update: MeasureDimensionPickerUpdate,
  ) => void;
  /** The dashboard painted, on the layout Luzmo picked for this viewer. */
  onRendered?: (screenMode: EmbeddedDashboardScreenMode) => void;
}

// One click can reach the page by two routes; anything closer than this is
// the same click, not a second one.
const DUPLICATE_ACTION_WINDOW_MS = 1000;

/**
 * Luzmo renderer. The only component that knows Luzmo's embed vocabulary —
 * everything above it works in terms of {provider, payload}.
 *
 * Two lessons are load-bearing here:
 * 1. `apiHost` must always be passed. The component defaults it to the EU
 *    region (https://api.luzmo.com), where a US authorization "does not
 *    exist" — the embed then refuses to load the dashboard.
 * 2. Auth props are pinned to the first session and refreshes go through
 *    `setAuthorization` on the element: changing the auth props re-initializes
 *    the component, visibly reloading the dashboard on every session refetch
 *    (the session hook revalidates on focus).
 *
 * Note: the dashboard element (v7) exposes no authorizationExpired event —
 * only the viz-item element does — so session expiry is handled by refetching
 * in useEmbeddedDashboardSession rather than reactively here.
 *
 * Links out of the dashboard (a tracking-table row to its shipment, a carrier
 * tracking URL) are opened here, not by the chart: see readDashboardAction in
 * ./utils for why a window opened from inside a custom chart is unusable.
 */
export function LuzmoDashboard(props: Props) {
  const { payload, onMeasureDimensionPickerChange, onRendered } = props;
  const posthog = usePostHog();
  const [error, setError] = useState<UIError>(null);
  // Luzmo's built-in loader shows two consecutive messages ("Initializing…",
  // "Loading dashboard…") on top of the loading state the page already showed
  // while the session was fetched — three visibly different loading states
  // for one dashboard. The embed offers no way to disable its loader, so it
  // is covered with the same skeleton the parent shows until the `load` event
  // fires; the user sees one continuous loading state instead.
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const dashboardRef = useRef<LuzmoEmbedDashboard>(null);
  // Pinned to the first session on purpose (the parent keys this component by
  // dashboard, so a payload change is always a session refresh): the setter is
  // unused because the render props must never change — changing auth props
  // re-initializes the embed, visibly reloading the dashboard on every
  // refetch. Refreshes flow through setAuthorization below instead.
  // eslint-disable-next-line react/hook-use-state
  const [initialPayload] = useState(payload);

  useEffect(() => {
    const dashboard = dashboardRef.current;
    if (dashboard === null || payload === initialPayload) {
      return;
    }
    // A fresh session supersedes whatever the previous one failed with.
    setError(null);
    dashboard.setAuthorization(payload.auth_key, payload.auth_token);
  }, [payload, initialPayload]);

  const handleLoad = useCallback(() => {
    setDashboardLoaded(true);
  }, []);

  // Luzmo re-emits itemsRendered on every repaint (a filter change, a resize
  // that crosses a screen-mode boundary), so the mode is reported on change
  // rather than per event: one report when the dashboard first paints, and one
  // more only if the viewer resizes onto a different canvas.
  const reportedScreenModeRef = useRef<EmbeddedDashboardScreenMode | null>(
    null,
  );
  const handleItemsRendered = useCallback(
    (event: Event) => {
      const screenMode = readRenderedScreenMode(event);
      if (screenMode === null || screenMode === reportedScreenModeRef.current) {
        return;
      }
      reportedScreenModeRef.current = screenMode;
      onRendered?.(screenMode);
    },
    [onRendered],
  );

  // A rejected token or an unreachable dataset otherwise renders as an empty
  // dashboard, so surface it to the user and to Sentry.
  const handleEmbedError = useCallback(
    (event: Event) => {
      const message = readEmbedErrorMessage(event);
      // Drop the loading cover so the error is not hidden behind the spinner.
      setDashboardLoaded(true);
      setError(`The dashboard could not be loaded: ${message}`);
      captureException(new Error(`Luzmo dashboard error: ${message}`), {
        extra: { dashboardId: payload.dashboard_id },
      });
    },
    [payload.dashboard_id],
  );

  // A custom chart asked the page to open something. Opened from this window,
  // which is not sandboxed, so the new tab keeps its origin and can log in.
  // A payload this page does not recognise is a contract mismatch between the
  // chart and this handler, so it is reported rather than dropped.
  //
  // If the browser blocks the new tab, a shipment still opens in this tab so
  // the click is never a no-op; an external tracking site is not worth losing
  // the dashboard for, so that case tells the user instead.
  //
  // The action can arrive by two routes (see readDashboardActionFromMessage in
  // ./utils): the documented `customEvent` element event, and the `message`
  // Luzmo re-posts on window, which is the only one the webComponent embed
  // delivers today. Both are handled so a Luzmo fix cannot silence the link,
  // and an identical action within a second is treated as the same click so
  // it cannot open the shipment twice either.
  const lastActionRef = useRef<{ url: string; at: number } | null>(null);
  const performDashboardAction = useCallback(
    (action: EmbeddedDashboardAction) => {
      const url = getDashboardActionUrl(action);
      const now = Date.now();
      const last = lastActionRef.current;
      if (
        last !== null &&
        last.url === url &&
        now - last.at < DUPLICATE_ACTION_WINDOW_MS
      ) {
        return;
      }
      lastActionRef.current = { url, at: now };

      const openedInNewTab = openInNewTab(url);
      if (
        !openedInNewTab &&
        action.type === EmbeddedDashboardActionType.OPEN_EXTERNAL_LINK
      ) {
        setError(
          "Your browser blocked the tracking link. Allow pop-ups for this site and try again.",
        );
        return;
      }
      if (!openedInNewTab) {
        navigateInThisTab(url);
      }
      posthog?.capture(ClientAppEvent.EMBEDDED_DASHBOARD_LINK_OPENED, {
        dashboardId: payload.dashboard_id,
        action: action.type,
        openedIn: openedInNewTab ? "new_tab" : "same_tab",
      });
    },
    [payload.dashboard_id, posthog],
  );

  const handleCustomEvent = useCallback(
    (event: Event) => {
      const action = readDashboardAction(event);
      if (action === null) {
        captureException(
          new Error("Luzmo dashboard emitted an unrecognised custom event"),
          {
            extra: {
              dashboardId: payload.dashboard_id,
              detail: event instanceof CustomEvent ? event.detail : null,
            },
          },
        );
        return;
      }
      performDashboardAction(action);
    },
    [payload.dashboard_id, performDashboardAction],
  );

  useEffect(() => {
    // Messages that are not a relayed custom event (the chart's own raw copy,
    // React's scheduler, other libraries) are not ours and are not reported.
    const handleRelayedMessage = (event: MessageEvent) => {
      const action = readDashboardActionFromMessage(event);
      if (action !== null) {
        performDashboardAction(action);
      }
    };
    window.addEventListener("message", handleRelayedMessage);
    return () => window.removeEventListener("message", handleRelayedMessage);
  }, [performDashboardAction]);

  // Only the picker action is of interest; the rest of `dashboardAction` is
  // editor activity this viewer-only embed never produces.
  const handleDashboardAction = useCallback(
    (event: Event) => {
      const update = readMeasureDimensionPickerUpdate(event);
      if (update !== null) {
        onMeasureDimensionPickerChange?.(update);
      }
    },
    [onMeasureDimensionPickerChange],
  );

  return (
    // minHeight comfortably exceeds the skeleton's summed minimums (~21rem)
    // so the loading cover always fits inside the host.
    <Box sx={{ width: "100%", position: "relative", minHeight: "24rem" }}>
      {error !== null && <ErrorTypography error={error} />}
      <LuzmoDashboardComponent
        ref={dashboardRef}
        apiHost={initialPayload.api_host}
        appServer={initialPayload.app_host}
        authKey={initialPayload.auth_key}
        authToken={initialPayload.auth_token}
        customEvent={handleCustomEvent}
        dashboardAction={handleDashboardAction}
        dashboardId={initialPayload.dashboard_id}
        // Renders in the page DOM rather than an iframe: better integration
        // and it lets product analytics see interactions.
        embedMode="webComponent"
        error={handleEmbedError}
        itemsRendered={handleItemsRendered}
        load={handleLoad}
        screenMode="auto"
      />
      {!dashboardLoaded && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            backgroundColor: EVOTRUX_SURFACE_WHITE,
          }}
        >
          <EmbeddedDashboardSkeleton />
        </Box>
      )}
    </Box>
  );
}
